import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import Api from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import type { IApiError } from 'src/helpers/request';
import notifications from 'src/redux/notifications/actions';
import actions from './actions';
import actionTypes from './actionTypes';
import { toFormData, toResult } from './adapter';
import {
  LIST_PAGE_SIZE,
  LIST_SCOPES,
  OPTION_LIST_PATHS,
  OPTION_LISTS,
  RUNS_PATH,
  RUN_ERROR_KINDS,
  RUN_UNPROCESSABLE,
  SIMULATOR_PATH,
} from './constants';
import type { OptionListKey, RunErrorKind } from './constants';
import { asRunValidations, deriveListScope, flattenValidations, normaliseSearch } from './helpers';
import type { FetchOptionsRequestAction, RunRequestAction } from './actions.d';
import type { IOption, IRunError, RunValidations } from './types';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_SERVER_ERROR_MIN = 500;

/** A success as the transport shapes it, or a refusal with the status it carried. */
interface IRunAnswer {
  status: number;
  data?: unknown;
  validations?: RunValidations | null;
  message?: string;
  failure?: never;
}

/** A rejection nothing answered: with no status to classify it by, the kind is decided at the rejection. */
interface IRunFailure {
  status: null;
  data?: never;
  validations?: never;
  message?: string;
  failure: RunErrorKind;
}

type IRunResponse = IRunAnswer | IRunFailure;

// Served outside the simulator's own permission, so its refusal must not read as a refusal of the
// page: the shared helper redirects to /forbidden on a 403 whose sentence says "not allowed".
const LISTS_OUTSIDE_PAGE_PERMISSION: readonly OptionListKey[] = [OPTION_LISTS.LEGAL_ENTITY];

const listApis = {
  [OPTION_LISTS.LEGAL_ENTITY]: new Api(OPTION_LIST_PATHS[OPTION_LISTS.LEGAL_ENTITY]),
  [OPTION_LISTS.LOCATION]: new Api(OPTION_LIST_PATHS[OPTION_LISTS.LOCATION]),
  [OPTION_LISTS.POSITION]: new Api(OPTION_LIST_PATHS[OPTION_LISTS.POSITION]),
  [OPTION_LISTS.AGENCY]: new Api(OPTION_LIST_PATHS[OPTION_LISTS.AGENCY]),
};

// `loadFormData` appends `/form_data`, so this is the namespace and not the runs collection.
const simulatorApi = new Api(SIMULATOR_PATH);
// `Api` is untyped JS whose methods demand every optional field, so the one method called directly rather than
// handed to `sendRequest` is narrowed to what this saga sends and reads.
const runsApi = new Api(RUNS_PATH) as unknown as { createItem: (args: { data: unknown }) => Promise<IRunResponse> };

const isOk = (result: { status?: number | null } | null | undefined): boolean =>
  typeof result?.status === 'number' && result.status >= 200 && result.status < 300;

// Responses are unwrapped by method: a GET keeps the payload at `data.success.data`, while a write
// has it spread onto `data`. Reading the GET path off a run reports every success as a failure.
const readData = (result: any): any => (isOk(result) ? result.data?.success?.data ?? null : null);

// The keys a 2xx carried, for the log line when they are not the ones expected; the values may be large.
// `typeof null` being "object" would otherwise describe an absent payload as one with keys.
const describeShape = (data: unknown): string => {
  if (data === null || data === undefined) return String(data);

  return typeof data === 'object' ? `keys ${JSON.stringify(Object.keys(data))}` : `a ${typeof data}`;
};

// `sendRequest` toasts a failure and returns nothing, so a missing result is a request that did not
// answer rather than one that answered oddly. Logged apart, or a 500 reads as a broken contract.
const describeMiss = (what: string, result: { status?: number | null } | undefined, data: unknown): string =>
  isOk(result)
    ? `${what} answered with an unexpected shape: ${describeShape(data)}`
    : `${what} did not answer (status ${result?.status ?? 'none'})`;

const readWrittenData = (result: IRunResponse | undefined): unknown => (isOk(result) ? result?.data ?? null : null);

export function* fetchOptions({ payload }: FetchOptionsRequestAction): Generator<any, void, any> {
  const { list, page = 1, scopeId = null } = payload;
  const listScope = LIST_SCOPES[list];
  // Normalised through the same helpers the reducer stamps the request with, so the guard there is
  // comparing two outputs of one rule rather than two spellings of it.
  const search = normaliseSearch(payload.search);
  const scope = deriveListScope(list, scopeId);

  try {
    // A list its endpoint refuses to serve unscoped would surface a spurious error rather than an
    // empty picker.
    if (listScope?.waitsForScope && !scopeId) {
      yield put(actions.fetchOptionsSuccess({ list, options: [], nextPage: null, append: false, search, scope }));

      return;
    }

    const requestSearch = {
      ...(search ? { search } : {}),
      page,
      page_size: LIST_PAGE_SIZE,
      ...(listScope && scopeId ? { [listScope.param]: scopeId } : {}),
    };

    // The shared notification action, not this feature's own: `sendRequest` cannot know which
    // list failed. The list-scoped error is dispatched below, where it can.
    const result = yield call(
      sendRequest,
      listApis[list].load,
      { search: requestSearch },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      LISTS_OUTSIDE_PAGE_PERMISSION.includes(list) ? { preserveErrorStatus: true } : undefined,
    );
    const data = readData(result);

    if (data && Array.isArray(data.records)) {
      const { records, pagination } = data;

      yield put(
        actions.fetchOptionsSuccess({
          list,
          options: records as IOption[],
          nextPage: pagination?.next_page ?? null,
          append: page > 1,
          search,
          scope,
        }),
      );

      return;
    }

    logger.error(`[rateSimulator] ${describeMiss(`${list} options`, result, data)}`);
    yield put(actions.fetchOptionsError({ list, search, scope }));
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchOptionsError({ list, search, scope }));
  }
}

// Loaded once with the page, not per run: the parity indicator reads the boundary while tenure
// weeks are still being typed.
export function* fetchFormData(): Generator<any, void, any> {
  try {
    const result = yield call(sendRequest, simulatorApi.loadFormData, {}, notifications.REQUEST_ERROR);
    const data = readData(result);

    if (data) {
      yield put(actions.fetchFormDataSuccess(toFormData(data)));

      return;
    }

    logger.error(`[rateSimulator] ${describeMiss('form data', result, data)}`);
    yield put(actions.fetchFormDataError());
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchFormDataError());
  }
}

// The status a rejection carries: `code` where the transport set it (every status but 401, 403, 500, 503 and a
// 404 whose body names no errors), else read off the message, which leads with it. Neither, and nothing answered.
const statusOf = (error: IApiError): number | null =>
  error?.code ?? (Number(error?.message?.match(/^\d{3}/)?.[0]) || null);

// A rejection with no status never had an answer read. The transport flags the one case where nothing
// answered; `JSON.parse` raises a SyntaxError where a body typed as JSON was not. Anything else is our own fault.
const failureOf = (error: IApiError): RunErrorKind => {
  if (error?.isTransportFailure) return RUN_ERROR_KINDS.NETWORK;

  if (error?.name === 'SyntaxError') return RUN_ERROR_KINDS.MALFORMED;

  logger.error(`[rateSimulator] a run rejected without a status: ${error?.name}: ${error?.message}`);

  return RUN_ERROR_KINDS.UNKNOWN;
};

// Resolves a refusal so the saga sees its status: `sendRequest` returns nothing, leaving a server fault
// indistinguishable from a lost connection. A 401 and a 403 still reject, so logout and forbidden still run.
const createRun = (args: { data: unknown }): Promise<IRunResponse> =>
  runsApi.createItem(args).catch((error: IApiError): IRunResponse => {
    const status = statusOf(error);

    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) throw error;

    // `detail`, not `message`: the message is built around the status line, and "404 Not Found" names
    // nothing a user can act on. Absent where the server sent no sentence of its own.
    if (status !== null) {
      return { status, validations: asRunValidations(error?.response), message: error?.detail };
    }

    return { status: null, message: error?.detail, failure: failureOf(error) };
  });

// What kind of failure a non-result is. No result at all is the helper returning nothing after it logged out
// on a 401 or redirected on a 403, which the card cannot name; a 2xx that reached here carried nothing readable.
const runErrorKind = (result: IRunResponse | undefined): RunErrorKind => {
  if (!result) return RUN_ERROR_KINDS.UNKNOWN;

  if (result.status === null) return result.failure;

  const { status } = result;

  if (status === RUN_UNPROCESSABLE) return RUN_ERROR_KINDS.VALIDATION;

  if (status === HTTP_FORBIDDEN) return RUN_ERROR_KINDS.FORBIDDEN;

  if (status >= HTTP_SERVER_ERROR_MIN) return RUN_ERROR_KINDS.SERVER;

  if (isOk(result)) return RUN_ERROR_KINDS.MALFORMED;

  return RUN_ERROR_KINDS.UNKNOWN;
};

// Only a 422 whose map holds a message to read names fields; one refusing the run as a whole is kept generic,
// or the card would point at highlighted fields with none lit. Its sentence is carried either way.
const runErrorOf = (result: IRunResponse | undefined): IRunError => {
  const kind = runErrorKind(result);

  if (kind !== RUN_ERROR_KINDS.VALIDATION) return { kind, message: result?.message };

  const validations = result?.validations ?? null;

  if (validations && flattenValidations(validations).some(([, messages]) => messages.length)) {
    return { kind, validations, message: result?.message };
  }

  return { kind: RUN_ERROR_KINDS.UNKNOWN, message: result?.message };
};

// `preserveErrorStatus` so a 403 arrives with its own sentence rather than the helper's generic toast, which
// this saga then owes. Every other refusal is resolved by `createRun` before the helper sees it.
export function* runSimulation({ payload }: RunRequestAction): Generator<any, void, any> {
  try {
    const result: IRunResponse | undefined = yield call(
      sendRequest,
      createRun,
      { data: { run: payload } },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      { preserveErrorStatus: true },
    );
    const data = readWrittenData(result);

    if (data) {
      yield put(actions.runSuccess(toResult(data)));

      return;
    }

    const error = runErrorOf(result);

    // The card explains a classified failure itself, so toasting is left to the two kinds where the server's
    // own sentence is all the user gets — and suppressed where it sent none.
    if (error.kind === RUN_ERROR_KINDS.FORBIDDEN || error.kind === RUN_ERROR_KINDS.UNKNOWN) {
      if (error.message) yield put(notifications.requestError({ error: error.message }));
    }

    yield put(actions.runError(error));
  } catch (error) {
    // Every refusal is resolved above, so a throw here is a fault of our own — the generic copy stands.
    logger.error(error);
    yield put(actions.runError());
  }
}

// `takeEvery`, not `takeLatest`: the pickers share one action type, so cancelling the latest would
// abort another list's in-flight fetch. The reducer drops answers to a superseded search or unit.
export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.FETCH_OPTIONS_REQUEST, fetchOptions),
    takeLatest(actionTypes.FETCH_FORM_DATA_REQUEST, fetchFormData),
    takeLatest(actionTypes.RUN_REQUEST, runSimulation),
  ]);
}
